import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { Parameter } from "../../models/Parameter";
import { SupplierQuotationService } from "../../services/SupplierQuotationService";
import { SupplierQuotationDTO } from "../../models/Quotation/SupplierQuotationDTO";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import { GetDocNumberDTO } from "../../models/Assistance/GetDocNumberDTO";
import { SelectItemsProps } from "../client/ClientEdit";
import { AssistanceService } from "../../services/AssistanceService";
import SearchSelectComponent from "../../components/SearchSelectComponent";
import { SupplierService } from "../../services/SupplierService";
import { SearchBySupplierNameDTO } from "../../models/Supplier/SearchBySupplierNameDTO";
import { PartsAndServicesQuotationDTO } from "../../models/Quotation/PartsAndServicesQuotationDTO";
import { UpdateStatusRequestDTO } from '../../models/Status/UpdateStatusRequestDTO'; 

export default function SupplierQuotationIndex() {
    const intl = useIntl();
    //Declaração de outros estados e variáveis importantes
    const navigate = useNavigate();
    const pageSizes: number[] = [5, 15, 30];
    const [data, setData] = useState<SupplierQuotationDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();
    const supplierService = new SupplierService();
    const assistanceService = new AssistanceService();
    const supplierQuotationService = new SupplierQuotationService();
    const [selectedSupplier, setSelectedSupplier] = useState<SelectItemsProps | null>(null);
    const [selectedAssistance, setSelectedAssistance] = useState<SelectItemsProps | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<SelectItemsProps | null>(null);
    const [notes, setNotes] = useState<string>("");
    const [reject, setReject] = useState<boolean>(false);
    const [quotation, setQuotation] = useState<number>(0);


    //Vamos tentar obter a page e a pageSize pelos query parameters
    //Usamos useState em vez de simple var, para não estar sempre a obter o valor a cada re-render, desta forma o page e o pageSize são sempre o mesmo
    //porque nunca estamos a fazer setState à var
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(
        Utilities.LoadPageSizeFromURLQuery(pageSizes)
    );

    //Search Parameters
    const [id, setId] = useState<string>(
        Utilities.LoadParameterFromURLQuery("id", "string", "")
    );
    const [supplier, setSupplier] = useState<string>(
        Utilities.LoadParameterFromURLQuery("supplier", "string", "")
    );
    const [status, setStatus] = useState<string>(
        Utilities.LoadParameterFromURLQuery("status", "string", "")
    );
    const [assistance, setAssistance] = useState<string>(
        Utilities.LoadParameterFromURLQuery("assistance", "string", "")
    );


    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<
        SortingRule<object>[] | null
    >(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder")
    );

    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) =>
                    OperationsCell(props.cell.row.original.id, "supplierQuotation"),
                width: 5,
            },
            {
                Header: <FormattedMessage id="Screens.SupplierQuotation.Table.Column1" />,
                accessor: "id",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.SupplierQuotation.Table.Column2" />,
                accessor: "supplier.name",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.SupplierQuotation.Table.Column3" />,
                accessor: "status.frontendName",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.SupplierQuotation.Table.Column4" />,
                accessor: "assistance",
                Cell: (props: { value: AssistanceDTO | null }) => {
                    if (!props.value) return null;
                    return (
                        <span>
                            {props.value.erpDocType} {props.value.erpSerie} {props.value.erpNumDoc}
                        </span>
                    );
                },
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.SupplierQuotation.Table.Column5" />,
                accessor: "partsAndServicesQuotations",
                Cell: (props: { value: PartsAndServicesQuotationDTO[] | null }) => {
                    if (!props.value) return null;
    
                    let totalValue = 0;
                    props.value.forEach((item) => {
                        totalValue += Utilities.calculateTotalPrice(item.costPrice, item.quantity);
                    });
    
                    return (
                        <span>
                            {totalValue}
                        </span>
                    );
                },
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.SupplierQuotation.Table.Column6" />,
                accessor: "supplierQuotation",
                width: 10,
                Cell: (props: any) =>
                    props.cell.row.original.status.frontendName === 'Em analise' ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <ButtonComponent
                                style={{ margin: 'auto', width: '30%' }}
                                text={intl.formatMessage({ id: "Screens.Quotation.Accept" })}
                                onClick={() => handleAccept(props.cell.row.original.id)}
                                disabled={false}
                            />
                            <ButtonComponent
                                style={{ margin: 'auto', width: '30%' }}
                                text={intl.formatMessage({ id: "Screens.Quotation.Reject" })}
                                onClick={() => handleReject(props.cell.row.original.id)}
                                disabled={false}
                            />
                        </div>
                    ) : <span>Sem ações disponiveis</span>,
            }
        ],
        []
    );


    const handleAccept = async (id: number) => {
        try {
            const requestData: UpdateStatusRequestDTO = { accept: true };

            await supplierQuotationService.UpdateStatus(id, requestData);

            Toast.Show("success",intl.formatMessage({ id: "Screens.SupplierQuotation.Info.Accept" }));

            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
        } catch (error) {
            Toast.Show("error","Falhou a aceitar o pedido de cotação.");
        }
    };
    
    const handleReject = async (id: number) => {
        setQuotation(id);
        setNotes("");
        setReject(prevReject => !prevReject);
    };

    const handleSubmitReject = async () => {
        try {
            if(notes === "") {
                Toast.Show("error",intl.formatMessage({ id: "Screens.SupplierQuotation.Info.RejectNotesMissing" }));
                return;
            }

            const requestData: UpdateStatusRequestDTO = { accept: false, notes: notes };
            setNotes("");
            setReject(prevReject => !prevReject);

            await supplierQuotationService.UpdateStatus(quotation, requestData);
            
            Toast.Show("success",intl.formatMessage({ id: "Screens.SupplierQuotation.Info.Reject" }));
    
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
        } catch (error) {
            Toast.Show("error","Falhou a rejeitar o pedido de cotação.");
        }
    };

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null
    ) => {
        //Atualizamos na history do browser a current page e pageSize

        var searchUrl: string = `?page=${page}&pageSize=${pageSize}` +
            `${Utilities.AddParamToQueryIfValue("id", "string", id)}` +
            `${Utilities.AddParamToQueryIfValue("supplier", "string", supplier)}` +
            `${Utilities.AddParamToQueryIfValue("status", "string", status)}` +
            `${Utilities.AddParamToQueryIfValue("assistance", "string", assistance)}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }

        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        if (searchParams.toString() === "") {
            navigate(`/supplierQuotations${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/supplierQuotations${searchUrl}`);
        }

        setLoading(true);
    //Vamos obter os dados
    var response = await supplierQuotationService.GetAll(
        mountSearchParameters(),
        Utilities.MountSortParameters(sortBy),
        page,
        pageSize
      );
  
      if (response.success !== true) {
        setData([]);
        setPageCount(0);
        setLoading(false);
        setCurrentPageSize(5);
        setCurrentPage(1);
        setCurrentSorting(null);
        Toast.Show("error", response.message);
        return;
      }
  
      //Atribuímos os dados
      setData(response.items);
      setPageCount(response.totalPages);
      setLoading(false);
      setCurrentPageSize(pageSize);
      setCurrentPage(page);
      setCurrentSorting(sortBy);
      setGoToPage(0);
  
      //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
      if (response.totalPages > 0 && page > response.totalPages) {
        setGoToPage(1);
      }
    };

    //Método para montar os parâmetros de pesquisa
    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];

        parameters.push(new Parameter("id", id));
        parameters.push(new Parameter("supplier", supplier));
        parameters.push(new Parameter("status", status));
        parameters.push(new Parameter("assistance", assistance));

        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setId("");
        setSupplier("");
        setSelectedSupplier(null);
        setStatus("");
        setAssistance("");
        setSelectedAssistance(null);
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }

        return () => { };
    }, [reloadSearch]);


    const searchSupplier = async (
        name: string
        ): Promise<SelectItemsProps[]> => {
            const searchParams: SearchBySupplierNameDTO = { name };
    
            const response = await supplierService.SearchByName(searchParams);
    
            if (response.success === false || response.obj === null) {
                Toast.Show("error", response.message);
            }
    
            const list: SelectItemsProps[] = [];
            response.obj?.forEach((element) => {
                const item: SelectItemsProps = {
                    value: element.id.toString(),
                    label: element.name,
                };
                list.push(item);
            });
            
            return list;
      };
    
  const searchAssistance = async (
    docNumber: number
    ): Promise<SelectItemsProps[]> => {
        const searchParams: GetDocNumberDTO = { docNumber };
            const response = await assistanceService.GetAssistanceIdByDoc(searchParams);
    
            if (response.success === false || response.obj === null) {
                Toast.Show("error", response.message);
            }
    
            const list: SelectItemsProps[] = [];
            response.obj?.forEach((element) => {
                const item: SelectItemsProps = {
                    value: element.id,
                    label: element.docType + " " + element.serie.toString() + " " + element.number.toString(),
                };
                list.push(item);
            });
            return list;
      };
      const [statusOptions] = useState<string[]>(["Aprovado", "Rejeitado", "Em analise"]);
      const statusOptionsList= async (): Promise<SelectItemsProps[]> => {
            const list: SelectItemsProps[] = [];
            statusOptions.forEach((element) => {
                const item: SelectItemsProps = {
                    value: element,
                    label: element,
                };
                list.push(item);
            });
            return list;
      };

      useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);

    const onNewTaskClick = () => {
        navigate(Routes.supplierQuotations.create);
    };


    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <span className="pageTitle">
                            <FormattedMessage id="Screens.SupplierQuotation.Title" />
                        </span>

                        <Row>
                            <Col
                                xl={2}
                                lg={3}
                                md={3}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", marginBottom: "1em" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.SupplierQuotation.Button.Create" })}
                                    onClick={onNewTaskClick}
                                    disabled={false} />
                            </Col>
                        </Row>
                    </div>

                    <div className="pageBody">
                        <Row>
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={id}
                                label={intl.formatMessage({ id: "Screens.SupplierQuotation.SearchField1" })}
                                readOnly={false}
                                onChange={(t: string) => setId(t)}
                            />
                            <SearchSelectComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                label={intl.formatMessage({ id: "Screens.SupplierQuotation.SearchField2" })}
                                loadOptions={searchSupplier}
                                onChange={(e: SelectItemsProps) => {
                                    setSelectedSupplier(e);
                                    setSupplier(e.value);
                                }}
                                value={selectedSupplier}
                            />
                            <SearchSelectComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                label={intl.formatMessage({
                                    id: "Screens.SupplierQuotation.SearchField3",
                                })}
                                loadOptions={statusOptionsList}
                                onChange={(e: SelectItemsProps) => {
                                    setSelectedStatus(e);
                                    setStatus(e.value);
                                }}
                                value={selectedStatus}
                                style={{ width: "100%" }}
                            />

                        <SearchSelectComponent
                            xl={2}
                            lg={3}
                            md={5}
                            sm={12}
                            xs={12}
                            label={intl.formatMessage({
                                id: "Screens.SupplierQuotation.SearchField4",
                            })}
                            loadOptions={(value: string) => searchAssistance(Number(value))}
                            onChange={(e: SelectItemsProps) => {
                                setSelectedAssistance(e);
                                setAssistance(e.value);
                            }}
                            value={selectedAssistance}
                            style={{ width: "100%" }}
                        />
                        </Row>

                        <Row>
                            <Col
                                xl={6}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                    style={{ minWidth: "100px" }}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                    style={{ minWidth: "113px" }}
                                />
                            </Col>
                        </Row>

                        <div className="pageComponent" style={{ width: "100%" }}>
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                                dontOrderFirstCollumn={true}
                            />
                        </div>

                        {reject ? (
                            <Row style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Col style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                <InputComponent
                                    xl={10}
                                    lg={10}
                                    md={10}
                                    sm={7}
                                    xs={6}
                                    type={"textArea"}
                                    label={intl.formatMessage({ id: "Screens.SupplierQuotation.Info.RejectNotes" })}
                                    readOnly={false}
                                    onChange={(t: string) => setNotes(t)}
                                    value={notes}

                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.Quotation.Reject" })}
                                    onClick={() => handleSubmitReject()}
                                />
                            </Col>
                        </Row>
                    ) : null}
                    </div>
                </div>
            </Layout>
        </>
    );
}
